import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import {
  FiatCurrency,
  InGameCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import marketCredit from '@sorare/core/src/assets/market-credit.svg';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Boost } from '@sorare/core/src/atoms/icons/Boost';
import { Card } from '@sorare/core/src/atoms/icons/Card';
import { Flame } from '@sorare/core/src/atoms/icons/Flame';
import { iconAlertCircle } from '@sorare/core/src/atoms/icons/system/iconAlertCircle.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { StatDisplay } from '@sorare/core/src/atoms/ui/StatDisplay';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import {
  Sum,
  useMonetaryAmount,
} from '@sorare/core/src/hooks/useMonetaryAmount';
import {
  fantasy,
  glossary,
  marketCredits,
} from '@sorare/core/src/lib/glossary';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';
import type { unitMapping } from '@sorare/core/src/lib/style';

import { ShardRewards } from '#src/components/rewards/NeonRewards/ShardRewards/index.tsx';

import type { NeonRewardConfigs_rewardConfig } from './__generated__/index.graphql.ts';

const Wrapper = styled(Horizontal)`
  justify-content: space-evenly;
  row-gap: var(--unit);
  flex-wrap: wrap;
  align-items: flex-start;
`;

type Props = {
  size?: 56 | 32 | 24 | 16;
  iconSize?: keyof typeof unitMapping;
  className?: string;
  displayLabels?: boolean;
  rewardConfigs: NeonRewardConfigs_rewardConfig[];
  compact?: boolean;
  verificationNeeded?: {
    kyc: boolean;
    phoneNumber: boolean;
  };
  myCompletedThresholdsStreakTasksCount?: number;
  myCommonCompletedThresholdsStreakTasksCount?: number;
};

const XP_CURRENCIES: InGameCurrency[] = [
  InGameCurrency.LIMITED_XP,
  InGameCurrency.RARE_XP,
  InGameCurrency.SUPER_RARE_XP,
  InGameCurrency.UNIQUE_XP,
];

export const NeonRewardConfigs = ({
  className,
  size = 56,
  iconSize = 4,
  displayLabels = true,
  rewardConfigs,
  compact,
  verificationNeeded,
  myCompletedThresholdsStreakTasksCount,
  myCommonCompletedThresholdsStreakTasksCount,
}: Props) => {
  const { formatNumber } = useIntlContext();
  const { toMonetaryAmount } = useMonetaryAmount();

  // CARD CONFIG
  const cardRewardConfigs = rewardConfigs.filter(isTypeFn('CardRewardConfig'));

  // SHARD CONFIG
  const shardRewardConfigs = rewardConfigs.filter(
    isTypeFn('CardShardRewardConfig')
  );

  // CASH CONFIG
  const cashRewardConfigs = rewardConfigs.filter(
    isTypeFn('MonetaryRewardConfig')
  );

  const totalCash = cashRewardConfigs.reduce((acc, rewardConfig) => {
    return (
      acc +
      (rewardConfig.sharedPool && rewardConfig.poolSize
        ? toMonetaryAmount(rewardConfig.monetaryAmount).usd /
          rewardConfig.poolSize
        : toMonetaryAmount(rewardConfig.monetaryAmount).usd)
    );
  }, 0);

  const conversionCreditRewardConfigs = rewardConfigs.filter(
    isTypeFn('ConversionCreditRewardConfig')
  );

  const totalConversionCredit = Sum(
    conversionCreditRewardConfigs.map(rewardConfig =>
      toMonetaryAmount(rewardConfig.maxDiscount)
    )
  ).usd;

  const formattedTotalCash = formatNumber(totalCash, {
    style: 'currency',
    currency: FiatCurrency.USD,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  });

  const formattedTotalConversionCredit = formatNumber(totalConversionCredit, {
    style: 'currency',
    currency: FiatCurrency.USD,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  });

  // SHARD CONFIG
  const xpRewardConfigs = rewardConfigs
    .filter(isTypeFn('InGameCurrencyRewardConfig'))
    .filter(({ currency }) => XP_CURRENCIES.includes(currency));
  const xpTotalCount = xpRewardConfigs.reduce(
    (acc, rewardConfig) => acc + rewardConfig.amount,
    0
  );
  const filteredRewards = [
    {
      id: 'cash',
      show: cashRewardConfigs.length > 0,
    },
    {
      id: 'cards',
      show: cardRewardConfigs.length > 0,
    },
    {
      id: 'shard',
      show: shardRewardConfigs.length > 0,
    },
    {
      id: 'xp',
      show: xpTotalCount > 0,
    },
    {
      id: 'conversionCredit',
      show: conversionCreditRewardConfigs.length > 0,
    },
  ].filter(({ show }) => !!show);

  const orderedRewards = filteredRewards.map(({ id }, index) => {
    const showNumber = !compact || index === 0;
    switch (id) {
      case 'cash':
        return (
          <StatDisplay
            key="cash"
            size={size}
            label={
              <Horizontal>
                {!!verificationNeeded?.kyc && (
                  <FontAwesomeIcon
                    icon={iconAlertCircle}
                    color="var(--c-yellow-400)"
                  />
                )}
                {!!displayLabels && (
                  <BodyS
                    color={
                      verificationNeeded?.kyc
                        ? 'var(--c-yellow-400)'
                        : undefined
                    }
                  >
                    <FormattedMessage {...glossary.cash} />
                  </BodyS>
                )}
              </Horizontal>
            }
          >
            {!!showNumber && formattedTotalCash}
          </StatDisplay>
        );
      case 'cards':
        return (
          <StatDisplay
            key="card"
            size={size}
            rotateIcon={false}
            icon={<Card size={iconSize} color="var(--c-rewards-purple)" />}
            label={
              <Horizontal>
                {!!verificationNeeded?.phoneNumber && (
                  <FontAwesomeIcon
                    icon={iconAlertCircle}
                    color="var(--c-yellow-400)"
                  />
                )}
                {!!displayLabels && (
                  <BodyS
                    color={
                      verificationNeeded?.phoneNumber
                        ? 'var(--c-yellow-400)'
                        : undefined
                    }
                  >
                    <FormattedMessage {...glossary.cards} />
                  </BodyS>
                )}
              </Horizontal>
            }
          >
            {!!showNumber && cardRewardConfigs.length}
          </StatDisplay>
        );
      case 'shard':
        return (
          <ShardRewards
            key="shard"
            shardRewardConfigs={shardRewardConfigs}
            size={size}
            iconSize={iconSize}
            displayLabels={displayLabels}
            showNumber={showNumber}
          />
        );
      case 'xp':
        return (
          <StatDisplay
            key="xp"
            size={size}
            icon={<Boost size={iconSize} color="var(--c-rewards-blue)" />}
            rotateIcon={false}
            label={
              <Horizontal>
                {!!displayLabels && (
                  <BodyS>
                    <FormattedMessage {...glossary.xp} />
                  </BodyS>
                )}
              </Horizontal>
            }
          >
            {!!showNumber && (
              <FormattedNumber value={xpTotalCount} notation="compact" />
            )}
          </StatDisplay>
        );
      case 'conversionCredit':
        return (
          <StatDisplay
            key="conversionCredit"
            rotateIcon={false}
            size={size}
            icon={
              <img
                src={marketCredit}
                alt=""
                style={{
                  width: `calc(${iconSize} * var(--unit))`,
                  height: `calc(${iconSize} * var(--unit))`,
                }}
              />
            }
            label={
              !!displayLabels && (
                <BodyS>
                  <FormattedMessage {...marketCredits.marketCredits} />
                </BodyS>
              )
            }
          >
            {!!showNumber && formattedTotalConversionCredit}
          </StatDisplay>
        );
      default:
        return null;
    }
  });

  if (orderedRewards.length === 0 && !myCompletedThresholdsStreakTasksCount) {
    return null;
  }
  return (
    <Wrapper className={className}>
      {orderedRewards.slice(0, 3)}
      {!!(
        myCompletedThresholdsStreakTasksCount ||
        myCommonCompletedThresholdsStreakTasksCount
      ) && (
        <StatDisplay
          key="card"
          size={size}
          rotateIcon={false}
          icon={
            <Flame
              size={iconSize}
              color={
                myCompletedThresholdsStreakTasksCount
                  ? undefined
                  : 'var(--c-white)'
              }
            />
          }
          label={
            !!displayLabels && (
              <Vertical gap={0.5}>
                <BodyS>
                  <FormattedMessage {...fantasy.hotStreak} />
                </BodyS>
                {!!myCompletedThresholdsStreakTasksCount &&
                  !!myCommonCompletedThresholdsStreakTasksCount && (
                    <LabelS color="var(--c-nd-700)">
                      (+ {myCommonCompletedThresholdsStreakTasksCount}{' '}
                      <FormattedMessage {...scarcityMessages[Rarity.common]} />)
                    </LabelS>
                  )}
              </Vertical>
            )
          }
        >
          {myCompletedThresholdsStreakTasksCount ||
            myCommonCompletedThresholdsStreakTasksCount}
        </StatDisplay>
      )}
    </Wrapper>
  );
};

NeonRewardConfigs.fragments = {
  rewardConfig: gql`
    fragment NeonRewardConfigs_rewardConfig on AnyRewardConfigInterface {
      id
      ... on MonetaryRewardConfig {
        id
        sharedPool
        poolSize
        monetaryAmount: amount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on CardShardRewardConfig {
        id
        ...ShardRewards_cardShardRewardConfig
      }
      ... on InGameCurrencyRewardConfig {
        id
        amount
        currency
      }
      ... on ConversionCreditRewardConfig {
        id
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${ShardRewards.fragments.cardShardRewardConfig}
  ` as TypedDocumentNode<NeonRewardConfigs_rewardConfig>,
};
